import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as FormIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import FormBuilder from './FormBuilder';
import { SEARCE_COLORS } from '../theme';

interface FormField {
  id: string;
  type: 'text' | 'dropdown' | 'checkbox';
  label: string;
  required: boolean;
  options?: string[];
  conditionalLogic?: {
    dependsOn: string;
    showWhen: string;
  };
}

interface Form {
  id: string;
  title: string;
  description?: string;
  form_schema: {
    fields: FormField[];
  };
  status: string;
  version: number;
  created_at: string;
  updated_at: string;
}

const Forms: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('forms');
  const [forms, setForms] = useState<Form[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  
  const navigate = useNavigate();


  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/${tab}`);
  };

  const handleCreateForm = () => {
    setCreateDialogOpen(true);
  };

  const handleEditForm = (form: Form) => {
    setSelectedForm(form);
    setShowFormBuilder(true);
  };

  const handlePublishForm = (formId: string) => {
    // TODO: Implement publish functionality
    // console.log('Publish form:', formId);
  };

  const handleCreateNewForm = () => {
    if (!formTitle.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a form title',
        severity: 'error'
      });
      return;
    }
    
    setSelectedForm({
      id: `form_${Date.now()}`,
      title: formTitle,
      description: formDescription,
      form_schema: { fields: [] },
      status: 'draft',
      version: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    setShowFormBuilder(true);
    setCreateDialogOpen(false);
    setFormTitle('');
    setFormDescription('');
  };

  const handleCancelCreate = () => {
    setCreateDialogOpen(false);
    setFormTitle('');
    setFormDescription('');
  };

  const handleSaveForm = (formData: any) => {
    const newForm: Form = {
      id: formData.id || `form_${Date.now()}`,
      title: formData.title,
      description: formData.description,
      form_schema: {
        fields: formData.fields || []
      },
      status: 'draft',
      version: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setForms(prevForms => [...prevForms, newForm]);
    setShowFormBuilder(false);
    setSnackbar({
      open: true,
      message: 'Form created successfully!',
      severity: 'success'
    });
  };

  const handleDeleteForm = (formId: string) => {
    setForms(prevForms => prevForms.filter(form => form.id !== formId));
    setSnackbar({
      open: true,
      message: 'Form deleted successfully!',
      severity: 'success'
    });
  };


  // Show form builder if requested
  if (showFormBuilder) {
    return (
      <FormBuilder
        onSave={handleSaveForm}
        initialForm={selectedForm ? {
          id: selectedForm.id,
          title: selectedForm.title,
          description: selectedForm.description || '',
          fields: selectedForm.form_schema.fields
        } : undefined}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: SEARCE_COLORS.background }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          marginLeft: '64px',
          transition: 'margin-left 0.3s ease-in-out',
          minHeight: '100vh',
          backgroundColor: SEARCE_COLORS.background,
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                color: SEARCE_COLORS.textPrimary,
                fontWeight: 700,
                mb: 1,
              }}
            >
              Custom Forms
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                fontSize: '1.1rem',
              }}
            >
              Create and manage custom forms for data collection and workflow automation.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateForm}
              sx={{
                backgroundColor: SEARCE_COLORS.primary,
                '&:hover': {
                  backgroundColor: SEARCE_COLORS.primaryDark,
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Create New Form
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              sx={{
                borderColor: SEARCE_COLORS.primary,
                color: SEARCE_COLORS.primary,
                '&:hover': {
                  borderColor: SEARCE_COLORS.primaryDark,
                  backgroundColor: 'rgba(0, 100, 255, 0.04)',
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Form Settings
            </Button>
          </Box>

          {/* Forms Grid */}
          <Grid container spacing={3}>
            {forms.length === 0 ? (
              /* Empty State */
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    border: `2px dashed ${SEARCE_COLORS.neutral_300}`,
                    borderRadius: 3,
                    backgroundColor: SEARCE_COLORS.white,
                  }}
                >
                  <FormIcon
                    sx={{
                      fontSize: 64,
                      color: SEARCE_COLORS.neutral_300,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: SEARCE_COLORS.textSecondary,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    No Forms Created Yet
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: SEARCE_COLORS.textSecondary,
                      mb: 3,
                      maxWidth: 400,
                      mx: 'auto',
                    }}
                  >
                    Get started by creating your first custom form. You can build forms for data collection,
                    surveys, feedback, and more.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateForm}
                    sx={{
                      backgroundColor: SEARCE_COLORS.primary,
                      '&:hover': {
                        backgroundColor: SEARCE_COLORS.primaryDark,
                      },
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    Create Your First Form
                  </Button>
                </Paper>
              </Grid>
            ) : (
              /* Forms List */
              forms.map((form) => (
                <Grid item xs={12} md={6} lg={4} key={form.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: SEARCE_COLORS.white,
                      border: '1px solid',
                      borderColor: SEARCE_COLORS.neutral_200,
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 100, 255, 0.15)',
                        borderColor: SEARCE_COLORS.primary,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: SEARCE_COLORS.textPrimary,
                          fontWeight: 600,
                          fontSize: '1.1rem',
                        }}
                      >
                        {form.title}
                      </Typography>
                      <Chip
                        label={form.status}
                        size="small"
                        color={form.status === 'published' ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    {form.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: SEARCE_COLORS.textSecondary,
                          mb: 2,
                        }}
                      >
                        {form.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: SEARCE_COLORS.textSecondary,
                        }}
                      >
                        Version {form.version} • Updated {new Date(form.updated_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit Form">
                        <IconButton
                          size="small"
                          onClick={() => handleEditForm(form)}
                          sx={{ color: SEARCE_COLORS.primary }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {form.status === 'draft' && (
                        <Tooltip title="Publish Form">
                          <IconButton
                            size="small"
                            onClick={() => handlePublishForm(form.id)}
                            sx={{ color: 'success.main' }}
                          >
                            <PublishIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Form">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteForm(form.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        </Container>
      </Box>

      {/* Create Form Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCancelCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Form</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: SEARCE_COLORS.textSecondary }}>
            Enter the basic details for your new form, then open the form builder to add fields and logic.
          </Typography>
          
          <TextField
            fullWidth
            label="Form Title"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Enter form title..."
            sx={{ mb: 3 }}
            required
          />
          
          <TextField
            fullWidth
            label="Form Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Enter form description (optional)..."
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<BuildIcon />}
            onClick={handleCreateNewForm}
            sx={{
              backgroundColor: SEARCE_COLORS.primary,
              '&:hover': {
                backgroundColor: SEARCE_COLORS.primaryDark,
              },
            }}
          >
            Open Form Builder
          </Button>
        </DialogActions>
      </Dialog>


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Forms;
